import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  createAccountingEntry,
  deleteAccountingEntry,
  editAccountingEntry,
} from '../../../../../../../../lib/api/accounting';
import CustomButton from '../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../lib/designSystem/CircularProgress';
import { LightTooltip } from '../../../../../../../../lib/designSystem/WhiteTooltip';
import {
  formatDate,
  hasAnyPermissions,
} from '../../../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../../../services/redux/authSlice';
import {
  AccountingEntryClass,
  AccountingEntryLine,
  AccountingNode,
  AccountingPeriod,
  EditEntryProps,
} from '../../../../types';
import CreateBusinessDialog from '../Importer/components/CreateBusinessDialog';
import { extractErrorMessage } from '../Importer/utils';
import EditEntryWithLines from './EditEntryWithLines';

const EditEntry = (props: EditEntryProps) => {
  const {
    entry,
    setEntry,
    open,
    setOpen,
    setUp = () => {},
    setSuccessText = () => {},
    setFailedText = () => {},
    nodes,
    entryClasses,
    accountingPeriods,
  } = props;

  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<AccountingPeriod | null>(
    null,
  );

  const editing = entry.id ? true : false;
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [missingRuts, setMissingRuts] = useState<string[]>([]);
  const [openCreateBusinessDialog, setOpenCreateBusinessDialog] =
    useState(false);
  const dispatch = useDispatch();

  // Function to check which period a date falls into
  const checkDateInPeriod = (date: string) => {
    if (!date || !accountingPeriods.length) {
      setSelectedPeriod(null);
      return;
    }

    const selectedDateObj = new Date(date);
    const period = accountingPeriods.find((p: AccountingPeriod) => {
      const startDate = new Date(p.startDate);
      const endDate = new Date(p.endDate);
      return selectedDateObj >= startDate && selectedDateObj <= endDate;
    });

    setSelectedPeriod(period || null);
  };

  // Handle date change
  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const date = event.target.value;
    setEntry({ ...entry, date });
    checkDateInPeriod(date);
  };

  const handleSave = async () => {
    if (!canSave()) return;

    if (entry.sourceKey) {
      await handleEdit();
    } else {
      await handleCreate();
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const day12Hours = new Date(entry.date);
      day12Hours.setUTCHours(12);
      const preparedEntry = prepareEntryForSubmission();
      const response = await createAccountingEntry(dispatch, {
        ...preparedEntry,
        date: day12Hours,
      });
      if (response?.id) {
        await setUp(response.id);
      } else {
        await setUp();
        setOpen(false);
      }
      setSuccessText('Asiento creado correctamente');
      setMissingRuts([]);
    } catch (error) {
      const errorResult = extractErrorMessage(error);
      if (errorResult?.missingRuts && errorResult.missingRuts.length > 0) {
        setMissingRuts(errorResult.missingRuts);
        setOpenCreateBusinessDialog(true);
      } else {
        setFailedText(errorResult?.message || 'Error al crear asiento');
      }
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteAccountingEntry(dispatch, entry.id);
      await setUp();
      setOpen(false);
      setSuccessText('Asiento eliminado correctamente');
    } catch (error) {
      setFailedText('Error al eliminar asiento');
    }
    setLoading(false);
  };

  const handleEdit = async () => {
    setLoading(true);
    try {
      const day12Hours = new Date(entry.date);
      day12Hours.setUTCHours(12);
      const preparedEntry = prepareEntryForSubmission();
      await editAccountingEntry(dispatch, {
        ...preparedEntry,
        date: day12Hours,
      });
      if (entry.id) {
        await setUp(entry.id);
      } else {
        await setUp();
        setOpen(false);
      }
      setSuccessText('Asiento editado correctamente');
    } catch (error) {
      setFailedText('Error al editar asiento');
    }
    setLoading(false);
  };

  const totalSumEquals = entry.lines.reduce(
    (acc: number, line: AccountingEntryLine) => {
      return acc + line.debit - line.credit;
    },
    0,
  );

  const hasName = entry.name !== '';

  // Get all required fields for the entire entry based on selected nodes
  const getRequiredFields = () => {
    const requiredFields = {
      costCenterRequired: false,
      documentKeyRequired: false,
      businessIdentifierRequired: false,
    };

    entry.lines.forEach((line: AccountingEntryLine) => {
      const node = nodes.find((n: AccountingNode) => n.id === line.nodeId);
      if (node) {
        if (node.costCenterRequired) requiredFields.costCenterRequired = true;
        if (node.documentKeyRequired) requiredFields.documentKeyRequired = true;

        if (node.businessIdentifierRequired)
          requiredFields.businessIdentifierRequired = true;
      }
    });

    return requiredFields;
  };

  const requiredFields = getRequiredFields();

  // Prepare entry for submission (filter out optional fields that are not required)
  const prepareEntryForSubmission = () => {
    const preparedLines = entry.lines.map((line: AccountingEntryLine) => {
      const node = nodes.find((n: AccountingNode) => n.id === line.nodeId);
      const preparedLine: AccountingEntryLine = {
        nodeId: line.nodeId,
        debit: line.debit,
        credit: line.credit,
        businessIdentifier: line.businessIdentifier,
        description: line.description,
      };

      // Only include required fields if the node requires them
      if (node) {
        if (node.costCenterRequired && line.costCenterId) {
          preparedLine.costCenterId = line.costCenterId;
        }
        if (node.documentKeyRequired && line.documentKey) {
          preparedLine.documentKey = line.documentKey;
        }
        if (node.businessIdentifierRequired && line.businessIdentifier) {
          preparedLine.businessIdentifier = line.businessIdentifier;
        }
      }

      return preparedLine;
    });

    return {
      ...entry,
      lines: preparedLines,
    };
  };

  const canSave = () => {
    if (!hasAnyPermissions(user, ['accounting_write_entries'])) return false;
    if (!hasName) return false;

    // Check if the selected period is closed
    if (selectedPeriod && selectedPeriod.status === 'CLOSED') return false;

    if (totalSumEquals !== 0) return false;

    if (entry.typeId === 'free') {
      if (entry.lines.length === 0) return false;
      if (entry.lines.some((line: AccountingEntryLine) => line.nodeId === null))
        return false;
    }

    if (
      entry.lines.some(
        (line: AccountingEntryLine) => line.debit === 0 && line.credit === 0,
      )
    )
      return false;

    // Validate conditional required fields based on node requirements
    for (const line of entry.lines) {
      const node = nodes.find((n: AccountingNode) => n.id === line.nodeId);
      if (node) {
        if (node.costCenterRequired && !line.costCenterId) return false;
        if (node.documentKeyRequired && !line.documentKey) return false;
        if (node.businessIdentifierRequired && !line.businessIdentifier)
          return false;
      }
    }

    return true;
  };

  // Check period when accounting periods are loaded and entry has a date
  useEffect(() => {
    if (accountingPeriods.length > 0 && entry.date) {
      checkDateInPeriod(entry.date);
    }
  }, [accountingPeriods, entry.date]);

  const handleSelectEntryClass = (e: SelectChangeEvent<string | null>) => {
    const selectedValue = e.target.value;
    if (!selectedValue) return;
    setEntry({
      ...entry,
      classId: selectedValue,
    });
  };

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog maxWidth="xl" open={open} onClose={() => setOpen(false)}>
      <DialogTitle>
        {editing ? 'Editar asiento #' + entry.id : 'Crear asiento'}
      </DialogTitle>
      <DialogContent style={{ minWidth: '1200px' }}>
        <Grid container spacing={2} paddingTop={2}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Clase de asiento</InputLabel>
              <Select
                disabled={loading || editing}
                fullWidth
                value={entry.classId}
                label="Clase de asiento"
                onChange={(e) => handleSelectEntryClass(e)}
              >
                {entryClasses.map((entryClass: AccountingEntryClass) => (
                  <MenuItem key={entryClass.id} value={entryClass.id}>
                    {entryClass.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Fecha"
              fullWidth
              type="date"
              value={entry.date}
              onChange={handleDateChange}
            />
          </Grid>
          <Grid item xs={12}>
            <LightTooltip title=" Los asientos pendientes no se incluyen en los balances hasta que sean guardados sin esta opción.">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={entry.status === 'PENDING'}
                    onChange={(e) =>
                      setEntry({
                        ...entry,
                        status: e.target.checked ? 'PENDING' : 'CLOSED',
                      })
                    }
                  />
                }
                label="Marcar como pendiente"
              />
            </LightTooltip>
          </Grid>
        </Grid>

        {/* Period Status Display */}
        {selectedPeriod && (
          <Alert
            severity={
              selectedPeriod.status === 'CLOSED' ? 'warning' : 'success'
            }
            sx={{ marginTop: 2 }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Período: {selectedPeriod.label}
            </Typography>
            <Typography variant="body2">
              Estado:{' '}
              {selectedPeriod.status === 'CLOSED' ? 'Cerrado' : 'Abierto'}
            </Typography>
            <Typography variant="body2">
              Rango:{' '}
              {formatDate(new Date(selectedPeriod.startDate), 'dd-mm-yyyy')} -{' '}
              {formatDate(new Date(selectedPeriod.endDate), 'dd-mm-yyyy')}
            </Typography>
            {selectedPeriod.status === 'CLOSED' && (
              <Typography
                variant="body2"
                sx={{ marginTop: 1, fontWeight: 'bold' }}
              >
                ⚠️ Este período está cerrado. No se pueden crear asientos
                contables en esta fecha.
              </Typography>
            )}
          </Alert>
        )}

        {entry.date && !selectedPeriod && (
          <Alert severity="info" sx={{ marginTop: 2 }}>
            <Typography variant="body1">
              No se encontró ningún período contable para la fecha seleccionada.
            </Typography>
          </Alert>
        )}
        <Divider style={{ marginTop: 20 }} />
        {
          <div style={{ marginTop: 20 }}>
            <EditEntryWithLines
              entry={entry}
              setEntry={setEntry}
              nodes={nodes}
              entryClasses={entryClasses}
              free={entry.typeId === 'free'}
              requiredFields={requiredFields}
            />
          </div>
        }
      </DialogContent>
      <DialogActions>
        {editing && (
          <CustomButton
            color="error"
            onClick={() => setOpenDeleteDialog(true)}
            disabled={!hasAnyPermissions(user, ['accounting_delete_entries'])}
          >
            Eliminar
          </CustomButton>
        )}
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton disabled={!canSave()} onClick={() => handleSave()}>
          Guardar
        </CustomButton>
      </DialogActions>
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Eliminar asiento</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar este asiento?
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setOpenDeleteDialog(false)}
          >
            Cancelar
          </CustomButton>
          <CustomButton color="error" onClick={() => handleDelete()}>
            Eliminar
          </CustomButton>
        </DialogActions>
      </Dialog>

      <CreateBusinessDialog
        open={openCreateBusinessDialog}
        onClose={() => {
          setOpenCreateBusinessDialog(false);
          setMissingRuts([]);
        }}
        dispatch={dispatch}
        missingRuts={missingRuts}
        onBusinessCreated={() => {
          setMissingRuts([]);
          setOpenCreateBusinessDialog(false);
        }}
      />
    </Dialog>
  );
};

export default EditEntry;
